/**
 * Theme delivery date picker (Pickeasy-compatible cart attributes).
 * Config: window.DeliveryDateConfig (delivery-date-settings.liquid)
 */
(function () {
  const DESKTOP_MQ = '(min-width: 1024px)';
  const WEEKDAY_NAMES = {
    sunday: 0,
    sun: 0,
    monday: 1,
    mon: 1,
    tuesday: 2,
    tue: 2,
    tues: 2,
    wednesday: 3,
    wed: 3,
    thursday: 4,
    thu: 4,
    thur: 4,
    thurs: 4,
    friday: 5,
    fri: 5,
    saturday: 6,
    sat: 6,
  };
  const MONTH_NAMES = {
    january: 0,
    jan: 0,
    february: 1,
    feb: 1,
    march: 2,
    mar: 2,
    april: 3,
    apr: 3,
    may: 4,
    june: 5,
    jun: 5,
    july: 6,
    jul: 6,
    august: 7,
    aug: 7,
    september: 8,
    sep: 8,
    sept: 8,
    october: 9,
    oct: 9,
    november: 10,
    nov: 10,
    december: 11,
    dec: 11,
  };
  const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  let cfg = null;
  let slotTimestamp = null;
  let selectedIso = null;
  let validDates = [];
  let validIsoSet = new Set();
  let cartItems = [];
  let cartItemCount = 0;
  let debounceTimer = null;
  let isUpdatingCart = false;
  let openPickerRoot = null;
  let onDocClick = null;
  let onDocKey = null;
  let onReposition = null;
  let sessionInitialized = false;

  function getCfg() {
    return window.DeliveryDateConfig || { enabled: false };
  }

  function parseLines(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return raw
      .split(/[\r\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function parseWeekdaySet(raw) {
    const set = new Set();
    parseLines(raw).forEach((line) => {
      const key = line.toLowerCase().replace(/\./g, '');
      if (WEEKDAY_NAMES[key] !== undefined) set.add(WEEKDAY_NAMES[key]);
    });
    return set;
  }

  function parseMonthSet(raw) {
    const set = new Set();
    parseLines(raw).forEach((line) => {
      const key = line.toLowerCase().replace(/\./g, '');
      if (MONTH_NAMES[key] !== undefined) set.add(MONTH_NAMES[key]);
    });
    return set;
  }

  function parseBlockedDates(raw) {
    const set = new Set();
    parseLines(raw).forEach((line) => {
      const iso = line.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) set.add(iso);
    });
    return set;
  }

  function parseBlockedRanges(raw) {
    const ranges = [];
    parseLines(raw).forEach((line) => {
      const parts = line.split(/[,|–-]+/).map((p) => p.trim().slice(0, 10));
      if (parts.length >= 2 && /^\d{4}-\d{2}-\d{2}$/.test(parts[0]) && /^\d{4}-\d{2}-\d{2}$/.test(parts[1])) {
        ranges.push({ start: parts[0], end: parts[1] });
      }
    });
    return ranges;
  }

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  function toIso(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function formatPickeasyDate(d) {
    return `${WEEKDAYS_SHORT[d.getDay()]}, ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  }

  /** Parse Pickeasy attribute e.g. "Fri, 3 Jul 2026" → ISO yyyy-mm-dd */
  function parsePickeasyDate(str) {
    if (!str || typeof str !== 'string') return null;
    const m = str.trim().match(/^[A-Za-zÀ-ÿ]{2,12},?\s+(\d{1,2})\s+([A-Za-zÀ-ÿ]{3,12})\s+(\d{4})$/i);
    if (!m) return null;
    const day = parseInt(m[1], 10);
    const monthKey = m[2].toLowerCase().replace(/\./g, '').slice(0, 3);
    const month = MONTH_NAMES[monthKey];
    const year = parseInt(m[3], 10);
    if (month === undefined || !day || !year) return null;
    const d = new Date(year, month, day);
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
    return toIso(d);
  }

  function getCartDateAttribute(cartLike) {
    const attrs = cartLike?.attributes || {};
    const keys = cfg?.cartAttributes || {};
    return (
      attrs[keys.orderDueDate] ||
      attrs[keys.translatedOrderDueDate] ||
      attrs['Order Due Date'] ||
      attrs['Translated Order Due Date'] ||
      ''
    );
  }

  function restoreSelectedFromCart(cartLike) {
    const raw = getCartDateAttribute(cartLike);
    const iso = parsePickeasyDate(raw);
    if (iso && validDates.some((d) => d.iso === iso)) {
      selectedIso = iso;
      return true;
    }
    return false;
  }

  function isoInRange(iso, startIso, endIso) {
    return iso >= startIso && iso <= endIso;
  }

  function isDateSelectable(d, rules) {
    const iso = toIso(d);
    const dow = d.getDay();
    const month = d.getMonth();

    if (rules.allowedWeekdays.size > 0 && !rules.allowedWeekdays.has(dow)) return false;
    if (rules.blockedWeekdays.has(dow)) return false;
    if (rules.blockedMonths.has(month)) return false;
    if (rules.blockedDates.has(iso)) return false;

    for (const range of rules.blockedRanges) {
      if (isoInRange(iso, range.start, range.end)) return false;
    }

    return true;
  }

  function productLeadTimeDays(items, prefix) {
    let max = 0;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^${escaped}(\\d+)-days$`, 'i');

    (items || []).forEach((item) => {
      (item.tags || []).forEach((tag) => {
        const m = String(tag).match(re);
        if (m) max = Math.max(max, parseInt(m[1], 10));
      });
    });

    return max;
  }

  function finalLeadTime(config, items) {
    const global = Math.max(0, parseInt(config.globalLeadTime, 10) || 0);
    const product = productLeadTimeDays(items, config.productTagPrefix || 'wait-');
    return Math.max(global, product);
  }

  function buildRules(config) {
    return {
      allowedWeekdays: parseWeekdaySet(config.allowedWeekdays),
      blockedWeekdays: parseWeekdaySet(config.blockedWeekdays),
      blockedMonths: parseMonthSet(config.blockedMonths),
      blockedDates: parseBlockedDates(config.blockedDates),
      blockedRanges: parseBlockedRanges(config.blockedRanges),
    };
  }

  function computeValidDates(config, items) {
    const rules = buildRules(config);
    const lead = finalLeadTime(config, items);
    const maxAhead = Math.min(365, Math.max(14, parseInt(config.maxDaysAhead, 10) || 120));
    const list = [];

    let cursor = startOfDay(new Date());
    cursor.setDate(cursor.getDate() + lead);

    const scanLimit = maxAhead + lead + 365;
    let scanned = 0;

    while (list.length < maxAhead && scanned < scanLimit) {
      if (isDateSelectable(cursor, rules)) {
        list.push({
          iso: toIso(cursor),
          label: formatPickeasyDate(cursor),
        });
      }
      cursor.setDate(cursor.getDate() + 1);
      scanned += 1;
    }

    return list;
  }

  function debounce(fn, ms = 120) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fn, ms);
  }

  async function fetchCart() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  function loadCartTagsFromDom() {
    const els = document.querySelectorAll('[data-delivery-date-cart-tags]');
    for (const el of els) {
      try {
        const data = JSON.parse(el.textContent);
        if (Array.isArray(data)) return data;
      } catch (_) {
        /* ignore */
      }
    }
    return null;
  }

  async function refreshCartContext() {
    try {
      const tagsFromDom = loadCartTagsFromDom();
      if (tagsFromDom) cartItems = tagsFromDom;

      const cart = await fetchCart();
      cartItemCount = cart?.item_count || 0;
      if (!tagsFromDom) {
        cartItems = (cart.items || []).map(() => ({ tags: [] }));
      }
      return cart;
    } catch (e) {
      console.warn('[delivery-date]', e);
      return null;
    }
  }

  async function clearCartDateAttributes() {
    if (isUpdatingCart) return;
    isUpdatingCart = true;
    const attrs = {
      [cfg.cartAttributes.orderDueDate]: '',
      [cfg.cartAttributes.translatedOrderDueDate]: '',
      [cfg.cartAttributes.orderDueTime]: '',
    };
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (e) {
      console.warn('[delivery-date] clear attributes failed', e);
    } finally {
      isUpdatingCart = false;
    }
  }

  function buildAttributePayload(formatted) {
    const timeVal = (cfg.orderDueTimeValue || 'All day').trim();
    const fulfillment = (cfg.fulfillmentTypeValue || '').trim();
    if (!slotTimestamp) slotTimestamp = String(Date.now());

    const attrs = {
      [cfg.cartAttributes.orderDueDate]: formatted,
      [cfg.cartAttributes.translatedOrderDueDate]: formatted,
      [cfg.cartAttributes.orderDueTime]: timeVal,
      [cfg.cartAttributes.source]: 'theme',
      [cfg.cartAttributes.slotTimestamp]: slotTimestamp,
    };

    if (fulfillment) {
      attrs[cfg.cartAttributes.fulfillmentType] = fulfillment;
    }

    return attrs;
  }

  async function updateCartAttributes(attrs) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
    if (!res.ok) throw new Error('cart/update.js failed');
    return res.json();
  }

  function selectDate(iso) {
    if (!iso || !validDates.some((d) => d.iso === iso)) return false;
    selectedIso = iso;
    syncAllTriggers(iso);
    syncCheckoutButtons();
    return true;
  }

  async function persistDate(iso) {
    if (!iso || isUpdatingCart) return false;
    const entry = validDates.find((d) => d.iso === iso);
    if (!entry) return false;

    isUpdatingCart = true;
    syncCheckoutButtons();
    const formatted = entry.label;
    const attrs = {
      'collection-pagination': null,
      'blog-pagination': null,
      'choose_option_id': null,
      'datetime-updated': null,
      'datetime-created': null,
      ...buildAttributePayload(formatted),
    };
    if (typeof CART_ATTRIBUTES !== 'undefined') {
      attrs[CART_ATTRIBUTES.product_comparison_options] = null;
      attrs[CART_ATTRIBUTES.product_comparison_metafields] = null;
    }

    try {
      await updateCartAttributes(attrs);
      selectedIso = iso;
      syncAllTriggers(iso);
      return true;
    } catch (e) {
      console.warn('[delivery-date] cart update failed', e);
      selectedIso = null;
      syncAllTriggers(null);
      updateErrors(true);
      return false;
    } finally {
      isUpdatingCart = false;
      syncCheckoutButtons();
    }
  }

  async function ensureCheckoutAttributes() {
    if (!hasValidSelection()) return false;
    const entry = validDates.find((d) => d.iso === selectedIso);
    if (!entry) return false;
    return persistDate(selectedIso);
  }

  function getCheckoutAttributes() {
    if (!hasValidSelection()) return null;
    const entry = validDates.find((d) => d.iso === selectedIso);
    if (!entry) return null;
    return buildAttributePayload(entry.label);
  }

  function localeCode() {
    return cfg?.locale || document.documentElement.lang || 'en';
  }

  function isoToDate(iso) {
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d);
  }

  function formatDisplayDate(iso) {
    const d = isoToDate(iso);
    try {
      return new Intl.DateTimeFormat(localeCode(), {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(d);
    } catch (_) {
      return formatPickeasyDate(d);
    }
  }

  function monthKey(y, m) {
    return y * 12 + m;
  }

  function monthBounds() {
    if (!validDates.length) return null;
    const first = isoToDate(validDates[0].iso);
    const last = isoToDate(validDates[validDates.length - 1].iso);
    return {
      min: monthKey(first.getFullYear(), first.getMonth()),
      max: monthKey(last.getFullYear(), last.getMonth()),
    };
  }

  function localizedWeekdays() {
    const names = [];
    try {
      const fmt = new Intl.DateTimeFormat(localeCode(), { weekday: 'short' });
      // 2024-01-07 is a Sunday; iterate a full week.
      for (let i = 0; i < 7; i += 1) {
        names.push(fmt.format(new Date(2024, 0, 7 + i)));
      }
    } catch (_) {
      return WEEKDAYS_SHORT.slice();
    }
    return names;
  }

  function localizedMonthTitle(y, m) {
    try {
      return new Intl.DateTimeFormat(localeCode(), { month: 'long', year: 'numeric' }).format(new Date(y, m, 1));
    } catch (_) {
      return `${MONTHS_SHORT[m]} ${y}`;
    }
  }

  function syncAllTriggers(iso) {
    document.querySelectorAll('[data-delivery-date-picker]').forEach((root) => {
      const valueEl = root.querySelector('[data-delivery-date-value]');
      const trigger = root.querySelector('[data-delivery-date-trigger]');
      if (valueEl) {
        if (iso) {
          valueEl.textContent = formatDisplayDate(iso);
          valueEl.removeAttribute('data-placeholder');
        } else {
          valueEl.textContent = cfg.strings?.placeholder || 'Pick a delivery date';
          valueEl.setAttribute('data-placeholder', 'true');
        }
      }
      if (trigger) trigger.classList.toggle('delivery-date-picker__trigger--has-value', !!iso);
    });
    // Refresh open / inline calendars.
    refreshCalendars();
  }

  function hasValidSelection() {
    return !!selectedIso && validDates.some((d) => d.iso === selectedIso);
  }

  function setBtnEnabled(btn, enabled) {
    if (!btn) return;
    if (enabled) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    } else {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.55';
    }
  }

  function updateErrors(show) {
    const msg = cfg.strings?.required || 'Please pick a delivery date to continue.';
    document.querySelectorAll('[data-delivery-date-error]').forEach((el) => {
      el.hidden = !show;
      el.textContent = show ? msg : '';
    });
  }

  function syncCheckoutButtons() {
    const ok = hasValidSelection() && !isUpdatingCart;
    const drawer = document.getElementById('CartDrawer');
    const drawerBtn = drawer?.querySelector('#checkout, .cart-drawer__checkout');
    const mainBtn = document.getElementById('MainCart__Checkout');
    const cartEmpty = cartItemCount === 0;

    if (!validDates.length && !cartEmpty) {
      setBtnEnabled(drawerBtn, false);
      setBtnEnabled(mainBtn, false);
      document.querySelectorAll('[data-delivery-date-error]').forEach((el) => {
        el.hidden = false;
        el.textContent = cfg.strings?.noDates || el.textContent;
      });
      return;
    }

    if (drawerBtn) setBtnEnabled(drawerBtn, ok);
    if (mainBtn) setBtnEnabled(mainBtn, ok);
    updateErrors(!ok && !cartEmpty);
  }

  function pickerMode(ctx) {
    if (ctx === 'desktop') return 'inline';
    if (ctx === 'mobile' || ctx === 'drawer') return 'expand';
    return 'popover';
  }

  function getPopover(root) {
    return root._ddPopoverEl || root.querySelector('[data-delivery-date-popover]');
  }

  function portalPopover(popover, root) {
    if (popover.parentElement === document.body) return;
    const marker = document.createComment('delivery-date-popover');
    popover.parentElement.insertBefore(marker, popover);
    root._ddPopoverMarker = marker;
    document.body.appendChild(popover);
    popover.classList.add('delivery-date-picker__popover--floating');
  }

  function unportalPopover(popover, root) {
    if (!root._ddPopoverMarker || popover.parentElement !== document.body) return;
    root._ddPopoverMarker.parentElement.insertBefore(popover, root._ddPopoverMarker);
    root._ddPopoverMarker.remove();
    delete root._ddPopoverMarker;
    popover.classList.remove('delivery-date-picker__popover--floating');
    popover.style.position = '';
    popover.style.width = '';
    popover.style.left = '';
    popover.style.top = '';
    popover.style.bottom = '';
  }

  function refreshCalendars() {
    document.querySelectorAll('[data-delivery-date-picker]').forEach((root) => {
      if (root.hidden) return;
      const popover = getPopover(root);
      if (!popover) return;
      const mode = root.dataset.deliveryDateMode || pickerMode(root.dataset.deliveryDateContext);
      if (mode !== 'inline' && popover.hidden) return;
      const y = parseInt(popover.dataset.viewY, 10);
      const m = parseInt(popover.dataset.viewM, 10);
      if (Number.isNaN(y) || Number.isNaN(m)) {
        const view = initialViewMonth();
        renderCalendar(popover, view.y, view.m);
      } else {
        renderCalendar(popover, y, m);
      }
    });
  }

  function bindPopoverInteractions(popover, root) {
    popover.addEventListener('click', (e) => {
      const nav = e.target.closest('[data-dd-nav]');
      if (nav) {
        if (nav.hasAttribute('disabled')) return;
        const y = parseInt(popover.dataset.viewY, 10);
        const m = parseInt(popover.dataset.viewM, 10);
        const delta = nav.dataset.ddNav === 'prev' ? -1 : 1;
        const next = new Date(y, m + delta, 1);
        renderCalendar(popover, next.getFullYear(), next.getMonth());
        if (root.dataset.deliveryDateMode === 'popover') positionPopover(root);
        return;
      }

      const dayBtn = e.target.closest('[data-dd-day]');
      if (dayBtn && !dayBtn.hasAttribute('disabled')) {
        const iso = dayBtn.dataset.ddDay;
        if (root.dataset.deliveryDateMode !== 'inline') closePopover();
        selectDate(iso);
        void persistDate(iso);
      }
    });
  }

  function attachOutsideListeners(root, popover, trigger) {
    onDocClick = (e) => {
      if (root.contains(e.target) || popover.contains(e.target)) return;
      closePopover();
    };
    onDocKey = (e) => {
      if (e.key === 'Escape') {
        closePopover();
        trigger?.focus?.();
      }
    };
    onReposition = () => positionPopover(root);
    requestAnimationFrame(() => {
      document.addEventListener('click', onDocClick, true);
      document.addEventListener('keydown', onDocKey, true);
    });
    if (root.dataset.deliveryDateMode === 'popover') {
      window.addEventListener('resize', onReposition);
      window.addEventListener('scroll', onReposition, true);
    }
  }

  function renderCalendar(popover, viewY, viewM) {
    const bounds = monthBounds();
    const todayIso = toIso(startOfDay(new Date()));
    popover.dataset.viewY = String(viewY);
    popover.dataset.viewM = String(viewM);

    const key = monthKey(viewY, viewM);
    const prevDisabled = bounds ? key <= bounds.min : true;
    const nextDisabled = bounds ? key >= bounds.max : true;

    const weekdays = localizedWeekdays();
    const firstDow = new Date(viewY, viewM, 1).getDay();
    const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();

    const prevLabel = cfg.strings?.prevMonth || 'Previous month';
    const nextLabel = cfg.strings?.nextMonth || 'Next month';

    let cells = '';
    for (let i = 0; i < firstDow; i += 1) {
      cells += '<span class="delivery-date-calendar__day delivery-date-calendar__day--empty" aria-hidden="true"></span>';
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const iso = toIso(new Date(viewY, viewM, day));
      const available = validIsoSet.has(iso);
      const selected = iso === selectedIso;
      const isToday = iso === todayIso;
      const classes = ['delivery-date-calendar__day'];
      if (available) classes.push('delivery-date-calendar__day--available');
      else classes.push('delivery-date-calendar__day--disabled');
      if (selected) classes.push('delivery-date-calendar__day--selected');
      if (isToday) classes.push('delivery-date-calendar__day--today');
      cells += `<button type="button" class="${classes.join(' ')}" data-dd-day="${iso}"${available ? '' : ' disabled aria-disabled="true"'}${selected ? ' aria-current="date"' : ''}>${day}</button>`;
    }

    const weekdayHead = weekdays
      .map((w) => `<span class="delivery-date-calendar__weekday">${w}</span>`)
      .join('');

    popover.innerHTML = `
      <div class="delivery-date-calendar">
        <div class="delivery-date-calendar__header">
          <button type="button" class="delivery-date-calendar__nav" data-dd-nav="prev" aria-label="${prevLabel}"${prevDisabled ? ' disabled' : ''}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"></path></svg>
          </button>
          <span class="delivery-date-calendar__title">${localizedMonthTitle(viewY, viewM)}</span>
          <button type="button" class="delivery-date-calendar__nav" data-dd-nav="next" aria-label="${nextLabel}"${nextDisabled ? ' disabled' : ''}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"></path></svg>
          </button>
        </div>
        <div class="delivery-date-calendar__weekdays">${weekdayHead}</div>
        <div class="delivery-date-calendar__grid">${cells}</div>
      </div>
    `;
  }

  function initialViewMonth() {
    const ref = selectedIso && validIsoSet.has(selectedIso) ? selectedIso : validDates[0]?.iso;
    if (!ref) {
      const now = new Date();
      return { y: now.getFullYear(), m: now.getMonth() };
    }
    const d = isoToDate(ref);
    return { y: d.getFullYear(), m: d.getMonth() };
  }

  function positionPopover(root) {
    const popover = getPopover(root);
    const trigger = root.querySelector('[data-delivery-date-trigger]');
    if (!popover || !trigger || root.dataset.deliveryDateMode !== 'popover') return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const desired = Math.min(360, popover.scrollHeight || 360);
    const placeAbove = spaceBelow < desired + 12 && rect.top > spaceBelow;
    popover.style.position = 'fixed';
    popover.style.width = `${Math.max(rect.width, 280)}px`;
    popover.style.left = `${rect.left}px`;
    if (placeAbove) {
      popover.style.top = 'auto';
      popover.style.bottom = `${window.innerHeight - rect.top + 6}px`;
    } else {
      popover.style.bottom = 'auto';
      popover.style.top = `${rect.bottom + 6}px`;
    }
  }

  function closePopover() {
    if (!openPickerRoot) return;
    const root = openPickerRoot;
    const popover = getPopover(root);
    const trigger = root.querySelector('[data-delivery-date-trigger]');
    const mode = root.dataset.deliveryDateMode;
    if (popover && mode !== 'inline') popover.hidden = true;
    if (trigger && mode !== 'inline') trigger.setAttribute('aria-expanded', 'false');
    if (popover && mode === 'popover') unportalPopover(popover, root);
    if (onDocClick) document.removeEventListener('click', onDocClick, true);
    if (onDocKey) document.removeEventListener('keydown', onDocKey, true);
    if (onReposition) {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    }
    onDocClick = null;
    onDocKey = null;
    onReposition = null;
    openPickerRoot = null;
  }

  function openPopover(root) {
    if (openPickerRoot && openPickerRoot !== root) closePopover();
    const popover = getPopover(root);
    const trigger = root.querySelector('[data-delivery-date-trigger]');
    if (!popover || !trigger) return;

    const mode = root.dataset.deliveryDateMode || 'popover';
    const view = initialViewMonth();
    renderCalendar(popover, view.y, view.m);
    popover.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    openPickerRoot = root;

    if (mode === 'popover') {
      portalPopover(popover, root);
      positionPopover(root);
    }

    attachOutsideListeners(root, popover, trigger);
  }

  function initPicker(root) {
    const trigger = root.querySelector('[data-delivery-date-trigger]');
    const popover = root.querySelector('[data-delivery-date-popover]');
    if (!trigger || !popover || root.dataset.deliveryDateBound === 'true') return;
    root.dataset.deliveryDateBound = 'true';
    root._ddPopoverEl = popover;

    const mode = pickerMode(root.dataset.deliveryDateContext);
    root.dataset.deliveryDateMode = mode;
    bindPopoverInteractions(popover, root);

    if (mode === 'inline') {
      root.classList.add('delivery-date-picker--inline');
      const view = initialViewMonth();
      renderCalendar(popover, view.y, view.m);
      popover.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      return;
    }

    if (mode === 'expand') root.classList.add('delivery-date-picker--expand');

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (openPickerRoot === root) closePopover();
      else openPopover(root);
    });
  }

  function updateDrawerPanelState() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    const onDesktop = window.matchMedia(DESKTOP_MQ).matches && cfg.showCartDrawer;
    const hasPicker = document.querySelector('[data-delivery-date-picker]');

    drawer.classList.toggle('cart-drawer--has-delivery-date', !!hasPicker);
    drawer.classList.toggle('cart-drawer--delivery-date-desktop-panel', onDesktop && !!hasPicker);
    drawer.classList.toggle('cart-drawer--has-order-slots', !!hasPicker);

    const panel = document.getElementById('CartDrawer__OrderSlotsPanel');
    if (panel) {
      let upsellOpen = false;
      try {
        upsellOpen = !!window.Alpine?.store('xMiniCart')?.isOpenUpsell;
      } catch (_) {}
      panel.classList.toggle('cart-drawer__order-slots-panel--beside-upsell', upsellOpen && onDesktop);
    }
  }

  function mountVisiblePickers() {
    document.querySelectorAll('[data-delivery-date-picker]').forEach((el) => {
      const ctx = el.dataset.deliveryDateContext;
      if (ctx === 'form' && !cfg.showCartForm) {
        el.hidden = true;
        return;
      }
      if (ctx === 'summary' && !cfg.showCartSummary) {
        el.hidden = true;
        return;
      }
      if ((ctx === 'drawer' || ctx === 'desktop' || ctx === 'mobile') && !cfg.showCartDrawer) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      initPicker(el);
    });
  }

  async function runSync() {
    cfg = getCfg();
    if (!cfg.enabled) return;

    cartItems = cfg.initialCart?.items || [];
    const cart = await refreshCartContext();

    validDates = computeValidDates(cfg, cartItems);
    validIsoSet = new Set(validDates.map((d) => d.iso));

    if (cartItemCount === 0) {
      selectedIso = null;
      sessionInitialized = true;
      if (getCartDateAttribute(cart) || getCartDateAttribute(cfg.initialCart)) {
        void clearCartDateAttributes();
      }
    } else if (!sessionInitialized) {
      // Full page load (drawer → /cart): restore saved attributes; do NOT clear.
      sessionInitialized = true;
      const restored = restoreSelectedFromCart(cart) || restoreSelectedFromCart(cfg.initialCart);
      if (!restored) {
        selectedIso = null;
        const stale = getCartDateAttribute(cart) || getCartDateAttribute(cfg.initialCart);
        if (stale) void clearCartDateAttributes();
      }
    } else if (selectedIso && !validDates.some((d) => d.iso === selectedIso)) {
      selectedIso = null;
      void clearCartDateAttributes();
    } else if (!selectedIso) {
      // SPA cart update: try restore if attributes exist but memory lost selection
      restoreSelectedFromCart(cart) || restoreSelectedFromCart(cfg.initialCart);
    }

    if (!slotTimestamp) {
      const ts =
        cart?.attributes?.[cfg.cartAttributes.slotTimestamp] ||
        cfg.initialCart?.attributes?.[cfg.cartAttributes.slotTimestamp];
      slotTimestamp = ts || String(Date.now());
    }

    mountVisiblePickers();
    syncAllTriggers(selectedIso);
    refreshCalendars();
    updateDrawerPanelState();
    syncCheckoutButtons();
  }

  function onCheckoutClick(e) {
    const target = e.target.closest('#checkout, .cart-drawer__checkout, #MainCart__Checkout');
    if (!target || !cfg?.enabled) return;

    if (!hasValidSelection()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      updateErrors(true);
      document.querySelector('[data-delivery-date-trigger]')?.focus?.();
      if (typeof Alpine !== 'undefined' && Alpine.store('xCartHelper')) {
        Alpine.store('xCartHelper').validated = false;
      }
      return;
    }

    if (typeof Alpine !== 'undefined' && Alpine.store('xCartHelper')) {
      Alpine.store('xCartHelper').validated = true;
    }
  }

  function bindListeners() {
    document.addEventListener('maximize:cart:updated', () => debounce(runSync));
    document.addEventListener('cart:updated', () => debounce(runSync));
    document.addEventListener('click', onCheckoutClick, true);

    if (typeof CART_EVENT !== 'undefined') {
      document.addEventListener(`${CART_EVENT.validate}`, () => {
        if (!hasValidSelection() && cartItemCount > 0) {
          updateErrors(true);
          if (typeof Alpine !== 'undefined' && Alpine.store('xCartHelper')) {
            Alpine.store('xCartHelper').validated = false;
          }
        }
      });
    }

    window.addEventListener('resize', () => debounce(updateDrawerPanelState));

    document.addEventListener('alpine:init', () => {
      if (typeof Alpine === 'undefined') return;
      let wasOpen = false;
      Alpine.effect(() => {
        const open = !!Alpine.store('xMiniCart')?.isOpen;
        if (open && !wasOpen) debounce(runSync, 50);
        wasOpen = open;
      });
      Alpine.effect(() => {
        if (Alpine.store('xMiniCart')?.isOpenUpsell !== undefined) {
          updateDrawerPanelState();
        }
      });
    });
  }

  function init() {
    cfg = getCfg();
    if (!cfg.enabled) return;

    bindListeners();
    runSync();
  }

  function selfCheck() {
    const problems = [];
    document.querySelectorAll('.delivery-date-calendar__day--available').forEach((btn) => {
      const iso = btn.dataset.ddDay;
      if (!validIsoSet.has(iso)) problems.push(`available day ${iso} not in validIsoSet`);
    });
    // Round-trip Pickeasy attribute parse
    const sample = formatPickeasyDate(new Date(2026, 6, 3));
    const roundTrip = parsePickeasyDate(sample);
    if (roundTrip !== '2026-07-03') problems.push(`parsePickeasyDate("${sample}") -> ${roundTrip}`);
    if (selectedIso) {
      const expected = formatPickeasyDate(isoToDate(selectedIso));
      const saved = getCheckoutAttributes?.()?.[cfg.cartAttributes.orderDueDate];
      if (saved && saved !== expected) {
        problems.push(`selected ${selectedIso} -> "${expected}" but attribute is "${saved}"`);
      }
    }
    if (problems.length) {
      console.error('[DeliveryDate] selfCheck failed:', problems);
      return false;
    }
    console.info('[DeliveryDate] selfCheck passed');
    return true;
  }

  window.DeliveryDateTheme = {
    sync: runSync,
    computeValidDates,
    formatPickeasyDate,
    getCheckoutAttributes,
    ensureCheckoutAttributes,
    selfCheck,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
